// Path: src/components/aols/aeo/OLSCalendarTime/MiniLightTimeToggle.tsx
// Updated to optimize performance, enhance accessibility, and integrate SEO keywords
// Optimized for macOS 11 and iPhone 12 (390x844), reflecting 05/49 PM +07, 06/04/25
// 06/04/25 05:49 PM +07

import React, { useState, useEffect } from "react";
import { Typography } from "antd";
import styled, { css } from "styled-components";

const { Text } = Typography;

const MiniToggleWrapper = styled.div<{ isDh: boolean }>`
  display: inline-block;
  padding: 5px 10px;
  background: ${({ isDh }) => (isDh ? "#000000" : "#ffff7f")};
  border: ${({ isDh }) => (isDh ? "2px solid #ff0000" : "none")};
  border-radius: 5px;
  transition: background 0.3s ease, border 0.3s ease;
  @media (max-width: 768px) {
    padding: 3px 8px;
  }
`;

const MiniToggleText = styled(Text, {
  shouldForwardProp: (prop) => prop !== "isDh",
})<{ isDh: boolean }>`
  color: ${({ isDh }) => (isDh ? "#ffffff" : "#0427f4")};
  font-weight: bold;
  font-size: 16px;
  @media (max-width: 768px) {
    font-size: 14px;
  }
  /* Ensure WCAG AA contrast ratio */
  ${props =>
    props.isDh
      ? css`color: #ffffff; background: #000000;`
      : css`color: #0427f4; background: #ffff7f;`}
`;

const timeData = [
  { old12: "12:00AM", old24: "00:00", lightTime: "7dh" },
  { old12: "1:00AM", old24: "1:00", lightTime: "8dh" },
  { old12: "2:00AM", old24: "2:00", lightTime: "9dh" },
  { old12: "3:00AM", old24: "3:00", lightTime: "10dh" },
  { old12: "4:00AM", old24: "4:00", lightTime: "11dh" },
  { old12: "5:00AM", old24: "5:00", lightTime: "12dh" },
  { old12: "6:00AM", old24: "6:00", lightTime: "1LH" },
  { old12: "7:00AM", old24: "7:00", lightTime: "2LH" },
  { old12: "8:00AM", old24: "8:00", lightTime: "3LH" },
  { old12: "9:00AM", old24: "9:00", lightTime: "4LH" },
  { old12: "10:00AM", old24: "10:00", lightTime: "5LH" },
  { old12: "11:00AM", old24: "11:00", lightTime: "6LH" },
  { old12: "12:00PM", old24: "12:00", lightTime: "7LH" },
  { old12: "1:00PM", old24: "13:00", lightTime: "8LH" },
  { old12: "2:00PM", old24: "14:00", lightTime: "9LH" },
  { old12: "3:00PM", old24: "15:00", lightTime: "10LH" },
  { old12: "4:00PM", old24: "16:00", lightTime: "11LH" },
  { old12: "5:00PM", old24: "17:00", lightTime: "12LH" },
  { old12: "6:00PM", old24: "18:00", lightTime: "1dh" },
  { old12: "7:00PM", old24: "19:00", lightTime: "2dh" },
  { old12: "8:00PM", old24: "20:00", lightTime: "3dh" },
  { old12: "9:00PM", old24: "21:00", lightTime: "4dh" },
  { old12: "10:00PM", old24: "22:00", lightTime: "5dh" },
  { old12: "11:00PM", old24: "23:00", lightTime: "6dh" },
];

const MiniLightTimeToggle: React.FC = () => {
  const [currentLightTime, setCurrentLightTime] = useState<string>("");

  useEffect(() => {
    const updateLightTime = () => {
      const now = new Date();
      const hours = now.getHours();
      const currentEntry = timeData[hours] || timeData[0]; // Fallback to first entry for invalid hours
      setCurrentLightTime(currentEntry.lightTime);
    };

    updateLightTime(); // Initial call
    const interval = setInterval(updateLightTime, 60000); // Update every minute

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  const isDh = currentLightTime.endsWith("dh");

  return (
    <MiniToggleWrapper isDh={isDh} aria-label={`Current light time: ${currentLightTime}, ${isDh ? 'daylight hours' : 'light hours'}`}>
      <MiniToggleText isDh={isDh}>{currentLightTime}</MiniToggleText>
    </MiniToggleWrapper>
  );
};

export default MiniLightTimeToggle;